import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  Typography,
  Avatar,
  Alert,
} from '@mui/material';
import {
  Person as PersonIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

/**
 * UserRoleSelector Component
 * Allows switching between role-based and user-based permission management
 */
const UserRoleSelector = ({
  mode,
  onModeChange,
  roles,
  users,
  selectedRole,
  selectedUser,
  onRoleChange,
  onUserChange,
  loading = false,
}) => {
  const { t } = useTranslation();

  // Debug logging
  React.useEffect(() => {
    if (selectedUser && users.length > 0) {
      const foundUser = users.find(u => u.id === selectedUser);
    }
  }, [mode, selectedUser, users]);

  // Debug logging
  React.useEffect(() => {
    if (selectedUser && users.length > 0) {
      const foundUser = users.find(u => u.id === selectedUser);
    }
  }, [mode, selectedUser, users]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Mode Toggle: Role or User */}
      <Box>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
          {t('access_management.permission_target')}
        </Typography>
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={(e, newMode) => {
            if (newMode !== null) {
              onModeChange(newMode);
            }
          }}
          fullWidth
          sx={{ mt: 1 }}
        >
          <ToggleButton 
            value="user"
            sx={{ 
              py: 1.5,
              '&.Mui-selected': {
                backgroundColor: '#667eea',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#5568d3',
                }
              }
            }}
          >
            <PersonIcon sx={{ mr: 1 }} />
            {t('access_management.manage_by_user')}
          </ToggleButton>
          <ToggleButton 
            value="role" 
            sx={{ 
              py: 1.5,
              '&.Mui-selected': {
                backgroundColor: '#667eea',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#5568d3',
                }
              }
            }}
          >
            <GroupsIcon sx={{ mr: 1 }} />
            {t('access_management.manage_by_role')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Role Selector */}
      {mode === 'role' && (
        <FormControl fullWidth>
          <InputLabel id="role-select-label">
            {t('access_management.select_role')}
          </InputLabel>
          <Select
            labelId="role-select-label"
            value={selectedRole || ''}
            onChange={(e) => onRoleChange(e.target.value)}
            label={t('access_management.select_role')}
            disabled={loading}
          >
            {Array.isArray(roles) && roles.length > 0 ? (
              roles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={role.name}
                      size="small"
                      sx={{
                        bgcolor:
                          role.name === 'SuperAdmin' ? '#d32f2f' :
                          role.name === 'CompanyAdmin' ? '#ff9800' :
                          role.name === 'Manager' ? '#2196f3' :
                          role.name === 'User' ? '#4caf50' :
                          '#757575',
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      ({role.userCount || 0} {t('access_management.users')})
                    </Typography>
                  </Box>
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>
                <Typography variant="body2" color="text.secondary">
                  {loading ? t('access_management.loading') : t('access_management.no_roles')}
                </Typography>
              </MenuItem>
            )}
          </Select>
        </FormControl>
      )}

      {/* User Selector */}
      {mode === 'user' && (
        <Box>
          <FormControl fullWidth>
            <InputLabel id="user-select-label">
              {t('access_management.select_user')}
            </InputLabel>
            <Select
              labelId="user-select-label"
              value={selectedUser || ''}
              onChange={(e) => {
                onUserChange(e.target.value);
              }}
              label={t('access_management.select_user')}
              disabled={loading}
              error={!selectedUser}
            >
              {Array.isArray(users) && users.length > 0 ? (
                users.map((user) => {
                  return (
                  <MenuItem key={user.id} value={user.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        sx={{ 
                          width: 32, 
                          height: 32, 
                          bgcolor: '#667eea',
                          fontSize: '0.875rem'
                        }}
                      >
                        {user.name?.charAt(0)?.toUpperCase() || 'U'}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {user.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.email} • {user.role}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                );
                })
              ) : (
                <MenuItem disabled>
                  <Typography variant="body2" color="text.secondary">
                    {loading ? t('access_management.loading') : t('access_management.no_users')}
                  </Typography>
                </MenuItem>
              )}
            </Select>
          </FormControl>
          
          {/* Validation message when no user is selected */}
          {!selectedUser && (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              {t('access_management.please_select_user') || 'Please select a user to manage their permissions'}
            </Alert>
          )}
        </Box>
      )}

      {/* Selected Info Display */}
      {mode === 'role' && selectedRole && (
        <Box
          sx={{
            p: 2,
            bgcolor: '#f5f5ff',
            borderRadius: 1,
            border: '1px solid #e0e0ff',
          }}
        >
          <Typography variant="caption" color="text.secondary" display="block">
            {t('access_management.configuring_permissions_for')}
          </Typography>
          <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
            {roles.find(r => r.id === selectedRole)?.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('access_management.affects_all_users_with_role')}
          </Typography>
        </Box>
      )}

      {mode === 'user' && selectedUser && (
        <Box
          sx={{
            p: 2,
            bgcolor: '#fff3e0',
            borderRadius: 1,
            border: '1px solid #ffe0b2',
          }}
        >
          <Typography variant="caption" color="text.secondary" display="block">
            {t('access_management.configuring_permissions_for')}
          </Typography>
          <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
            {users.find(u => u.id === selectedUser)?.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('access_management.user_specific_overrides')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default UserRoleSelector;
